import React, { useEffect, useState } from 'react';
import ProductUpload from './productUpload';
import ViewToggleButton from './toggleView';
import useAccount from '@/lib/hooks/useAccount';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import { mediumDesktop, mobile, semiLargeDesktop } from '@/helpers/utils/screensizes';
import { eventEmitter } from '@/components/organisms/project-builder/add-prod-to-project/EventEmitter';
import ProjectBuilderProductListAlgoliaTastic from '@/lib/tastics/project-builder-page/products/project-builder-product-list-algolia';
import { Tooltip } from 'react-tooltip';

type FacetConfiguration = {
    key: string;
    type: 'boolean' | 'range' | 'color' | 'term';
    label: string;
};

const AddProducts: React.FC<any> = ({ projectForm }) => {
    const { account } = useAccount();
    const [isMediumDesktop] = useMediaQuery(mediumDesktop);
    const [issemiLargeDesktop] = useMediaQuery(semiLargeDesktop);
    const [isMobile] = useMediaQuery(mobile);
    const [hideParentMsg, setHideParentMsg] = useState(false);
    const handleButtonClick = () => {
        window.location.href = '/';
    };
    const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
    const toggleView = () => {
        setViewMode((prevMode) => (prevMode === 'grid' ? 'list' : 'grid'));
    };
    const datavalue = {
        "mobile": true,
        "tablet": true,
        "desktop": true,
        "facetsConfiguration": [
            // Common
            { "key": "categories.en-AU.lvl0", "type": "term", "label": "Category" },
            { "key": "categories.en-AU.lvl1", "type": "term", "label": "Sub Category" },
            { "key": "categories.en-NZ.lvl0", "type": "term", "label": "Category" },
            { "key": "categories.en-NZ.lvl1", "type": "term", "label": "Sub Category" },
            { "key": "searchableAttributes.brand", "type": "term", "label": "Brand" },
            { "key": "searchableAttributes.product-types", "type": "term", "label": "Product Type" },
            { "key": "searchableAttributes.range", "type": "term", "label": "Range" },
            { "key": "searchableAttributes.item-material", "type": "term", "label": "Material" },
            { "key": "searchableAttributes.colour", "type": "term", "label": "Colour" },
            { "key": "attributes.number-of-tap-holes", "type": "term", "label": "Item Tapholes" },
            { "key": "attributes.overflow", "type": "term", "label": "Overflow" },
            { "key": "attributes.mount-type", "type": "term", "label": "Mount Type" },
            // Sanitaryware
            { "key": "attributes.clean-flush", "type": "term", "label": "Clean Flush" },
            { "key": "attributes.inlet-type", "type": "term", "label": "Inlet Type" },
            { "key": "attributes.pan-height", "type": "term", "label": "Pan Height" },
            { "key": "attributes.pan-projection-size", "type": "term", "label": "Pan Projection" },
            { "key": "attributes.trap-type", "type": "term", "label": "Trap Type" },
            // Tapware
            { "key": "attributes.litres-per-minute", "type": "term", "label": "Litres per Minute" },
            { "key": "attributes.coating-method", "type": "term", "label": "Coating Method" },
            { "key": "attributes.outlet-length", "type": "term", "label": "Outlet Length" },
            { "key": "attributes.outlet-type", "type": "term", "label": "Outlet Type" },
            { "key": "attributes.temperature-adjustable", "type": "term", "label": "Temperature Adjustable" },
            { "key": "attributes.flow-adjustable", "type": "term", "label": "Flow Adjustable" },
            { "key": "attributes.cartridge-type", "type": "term", "label": "Cartridge Type" },
            { "key": "attributes.cartidge-size", "type": "term", "label": "Cartridge Size" },
            // Showers
            { "key": "attributes.wels-with-flow-rate-flush-volume-1", "type": "term", "label": "WELS with Flow Rate" },
            { "key": "attributes.wels-with-flow-rate-flush-volume-2", "type": "term", "label": "WELS with Flow Rate #2" },
            { "key": "attributes.shower-type", "type": "term", "label": "Shower Type" },
            { "key": "attributes.spray-functions", "type": "term", "label": "Spray Function" },
            { "key": "attributes.number-of-spray-functions", "type": "term", "label": "Number of Spray Functions" },
            { "key": "attributes.shower-head-diameter", "type": "term", "label": "Shower Head Size" },
            { "key": "attributes.rail-length", "type": "term", "label": "Rail Length" },
            { "key": "attributes.rail-cutdown", "type": "term", "label": "Rail Cutdown" },
            { "key": "attributes.is-retrofit", "type": "term", "label": "Retrofit" },
            // Basins / Kitchen Sinks / Laundry Tubs / Baths
            { "key": "attributes.shape", "type": "term", "label": "Shape" },
            { "key": "attributes.capacity-to-overflow", "type": "term", "label": "Capacity to Overflow" },
            { "key": "attributes.capacity-size", "type": "term", "label": "Capacity Size" },
            { "key": "attributes.pedestal-available", "type": "term", "label": "Pedestal Available" },
            { "key": "attributes.bowl-position", "type": "term", "label": "Bowl Position" },
            { "key": "attributes.drainer", "type": "term", "label": "Drainer" },
            // Valves & Fittings
            { "key": "attributes.tmv-inlet-type", "type": "term", "label": "TMV Inlet Type" },
            { "key": "attributes.inlet-connection", "type": "term", "label": "Inlet Connection" },
            { "key": "attributes.outlet-connection", "type": "term", "label": "Outlet Connection" },
            { "key": "attributes.nominal-factory-pressure-setting", "type": "term", "label": "Nominal Factory Pressure Setting" },
            { "key": "attributes.nominal-factory-temperature-setting", "type": "term", "label": "Nominal Factory Temperature Setting" },
            { "key": "attributes.valve-type", "type": "term", "label": "Valve Type" },
            { "key": "attributes.maximum-operating-pressure", "type": "term", "label": "Maximum Operating Pressure" },
            { "key": "attributes.maximum-operating-temperature", "type": "term", "label": "Maximum Operating Temperature" },
            { "key": "attributes.minimum-operating-pressure", "type": "term", "label": "Minimum Operating Pressure" },
            { "key": "attributes.minimum-operating-temperature", "type": "term", "label": "Minimum Operating Temperature" },
            // Compliance (all locales)
            { "key": "attributes.IsIndependentLivingCompliant", "type": "term", "label": "Independent Living Compliant" },
            { "key": "attributes.IsAS14281Compliant", "type": "term", "label": "AS1428.1 Accessible" },
            { "key": "attributes.IsAS14281Ambulant", "type": "term", "label": "AS1428.1 Ambulant" },
            { "key": "attributes.IsNZ41121Compliant", "type": "term", "label": "NZ4121 Accessible" },
        ] as FacetConfiguration[],
        "pricesConfiguration": []
    };

    const propValue = {
        "params": {
            "locale": "au",
            "slug": [
                "search"
            ]
        },
        "searchParams": {
            "query": ""
        },
        "categories": [
            {
                "categoryId": "sanitaryware",
                "name": "Sanitaryware",
                "slug": "sanitaryware",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "urinals",
                        "parentId": "sanitaryware",
                        "name": "Urinals",
                        "slug": "urinals",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/urinals"
                    },
                    {
                        "categoryId": "bidet-suites",
                        "parentId": "sanitaryware",
                        "name": "Bidet Suites",
                        "slug": "bidet-suites",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/bidet-suites"
                    },
                    {
                        "categoryId": "toilet-suites",
                        "parentId": "sanitaryware",
                        "name": "Toilet Suites",
                        "slug": "toilet-suites",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/toilet-suites"
                    },
                    {
                        "categoryId": "cisterns",
                        "parentId": "sanitaryware",
                        "name": "Cisterns",
                        "slug": "cisterns",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/cisterns"
                    },
                    {
                        "categoryId": "toilet-pans",
                        "parentId": "sanitaryware",
                        "name": "Toilet Pans",
                        "slug": "toilet-pans",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/toilet-pans"
                    },
                    {
                        "categoryId": "toilet-seats",
                        "parentId": "sanitaryware",
                        "name": "Toilet Seats",
                        "slug": "toilet-seats",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/toilet-seats"
                    },
                    {
                        "categoryId": "care-toilets",
                        "parentId": "sanitaryware",
                        "name": "Care Toilets",
                        "slug": "care-toilets",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/care-toilets"
                    },
                    {
                        "categoryId": "buttons-accessories",
                        "parentId": "sanitaryware",
                        "name": "Buttons Accessories",
                        "slug": "buttons-accessories",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/sanitaryware/buttons-accessories"
                    }
                ],
                "_url": "/sanitaryware"
            },
            {
                "categoryId": "tapware",
                "name": "Tapware",
                "slug": "tapware",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "three-piece-tapware",
                        "parentId": "tapware",
                        "name": "Three Piece Tapware",
                        "slug": "three-piece-tapware",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/three-piece-tapware"
                    },
                    {
                        "categoryId": "basin-mixers",
                        "parentId": "tapware",
                        "name": "Basin Mixers",
                        "slug": "basin-mixers",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/basin-mixers"
                    },
                    {
                        "categoryId": "bath-shower-mixers",
                        "parentId": "tapware",
                        "name": "Bath Shower Mixers",
                        "slug": "bath-shower-mixers",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/bath-shower-mixers"
                    },
                    {
                        "categoryId": "kitchen-tapware",
                        "parentId": "tapware",
                        "name": "Kitchen Tapware",
                        "slug": "kitchen-tapware",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/kitchen-tapware"
                    },
                    {
                        "categoryId": "laundry-tapware",
                        "parentId": "tapware",
                        "name": "Laundry Tapware",
                        "slug": "laundry-tapware",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/laundry-tapware"
                    },
                    {
                        "categoryId": "care-tapware",
                        "parentId": "tapware",
                        "name": "Care Tapware",
                        "slug": "care-tapware",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/care-tapware"
                    },
                    {
                        "categoryId": "tapware-assemblies",
                        "parentId": "tapware",
                        "name": "Tapware Assemblies",
                        "slug": "tapware-assemblies",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/tapware-assemblies"
                    },
                    {
                        "categoryId": "outlets-spouts",
                        "parentId": "tapware",
                        "name": "Outlets Spouts",
                        "slug": "outlets-spouts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/tapware/outlets-spouts"
                    }
                ],
                "_url": "/tapware"
            },
            {
                "categoryId": "showers",
                "name": "Showers",
                "slug": "showers",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "shower-systems",
                        "parentId": "showers",
                        "name": "Shower Systems",
                        "slug": "shower-systems",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/showers/shower-systems"
                    },
                    {
                        "categoryId": "shower-heads-outlets",
                        "parentId": "showers",
                        "name": "Shower Heads Outlets",
                        "slug": "shower-heads-outlets",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/showers/shower-heads-outlets"
                    },
                    {
                        "categoryId": "shower-bases",
                        "parentId": "showers",
                        "name": "Shower Bases",
                        "slug": "shower-bases",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/showers/shower-bases"
                    },
                    {
                        "categoryId": "care-showers",
                        "parentId": "showers",
                        "name": "Care Showers",
                        "slug": "care-showers",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/showers/care-showers"
                    }
                ],
                "_url": "/showers"
            },
            {
                "categoryId": "basins",
                "name": "Basins",
                "slug": "basins",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "care-basins",
                        "parentId": "basins",
                        "name": "Care Basins",
                        "slug": "care-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/care-basins"
                    },
                    {
                        "categoryId": "wall-mounted-basins",
                        "parentId": "basins",
                        "name": "Wall Mounted Basins",
                        "slug": "wall-mounted-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/wall-mounted-basins"
                    },
                    {
                        "categoryId": "inset-basins",
                        "parentId": "basins",
                        "name": "Inset Basins",
                        "slug": "inset-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/inset-basins"
                    },
                    {
                        "categoryId": "shroud-pedestal-basins",
                        "parentId": "basins",
                        "name": "Shroud Pedestal Basins",
                        "slug": "shroud-pedestal-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/shroud-pedestal-basins"
                    },
                    {
                        "categoryId": "above-counter-basins",
                        "parentId": "basins",
                        "name": "Above Counter Basins",
                        "slug": "above-counter-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/above-counter-basins"
                    },
                    {
                        "categoryId": "semi-recessed-basins",
                        "parentId": "basins",
                        "name": "Semi Recessed Basins",
                        "slug": "semi-recessed-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/semi-recessed-basins"
                    },
                    {
                        "categoryId": "under-counter-basins",
                        "parentId": "basins",
                        "name": "Under Counter Basins",
                        "slug": "under-counter-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/under-counter-basins"
                    },
                    {
                        "categoryId": "vanity-basins",
                        "parentId": "basins",
                        "name": "Vanity Basins",
                        "slug": "vanity-basins",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/basins/vanity-basins"
                    }
                ],
                "_url": "/basins"
            },
            {
                "categoryId": "kitchen-sinks-accessories",
                "name": "Kitchen Sinks Accessories",
                "slug": "kitchen-sinks-accessories",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "kitchen-accessories",
                        "parentId": "kitchen-sinks-accessories",
                        "name": "Kitchen Accessories",
                        "slug": "kitchen-accessories",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/kitchen-sinks-accessories/kitchen-accessories"
                    },
                    {
                        "categoryId": "sinks",
                        "parentId": "kitchen-sinks-accessories",
                        "name": "Sinks",
                        "slug": "sinks",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/kitchen-sinks-accessories/sinks"
                    },
                    {
                        "categoryId": "care-sinks",
                        "parentId": "kitchen-sinks-accessories",
                        "name": "Care Sinks",
                        "slug": "care-sinks",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/kitchen-sinks-accessories/care-sinks"
                    }
                ],
                "_url": "/kitchen-sinks-accessories"
            },
            {
                "categoryId": "laundry-tubs",
                "name": "Laundry Tubs",
                "slug": "laundry-tubs",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "tubs",
                        "parentId": "laundry-tubs",
                        "name": "Tubs",
                        "slug": "tubs",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/laundry-tubs/tubs"
                    },
                    {
                        "categoryId": "tubs-cabinets",
                        "parentId": "laundry-tubs",
                        "name": "Tubs Cabinets",
                        "slug": "tubs-cabinets",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/laundry-tubs/tubs-cabinets"
                    }
                ],
                "_url": "/laundry-tubs"
            },
            {
                "categoryId": "baths",
                "name": "Baths",
                "slug": "baths",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "baths-l2",
                        "parentId": "baths",
                        "name": "Baths",
                        "slug": "baths-l2",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/baths/baths-l2"
                    },
                    {
                        "categoryId": "spa-baths",
                        "parentId": "baths",
                        "name": "Spa Baths",
                        "slug": "spa-baths",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/baths/spa-baths"
                    }
                ],
                "_url": "/baths"
            },
            {
                "categoryId": "integrated-solutions",
                "name": "Integrated Solutions",
                "slug": "integrated-solutions",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "connected-hardware",
                        "parentId": "integrated-solutions",
                        "name": "Connected Hardware",
                        "slug": "connected-hardware",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/integrated-solutions/connected-hardware"
                    },
                    {
                        "categoryId": "services",
                        "parentId": "integrated-solutions",
                        "name": "Services",
                        "slug": "services",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/integrated-solutions/services"
                    }
                ],
                "_url": "/integrated-solutions"
            },
            {
                "categoryId": "valves-fittings",
                "name": "Valves Fittings",
                "slug": "valves-fittings",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "expansion-control-valve",
                        "parentId": "valves-fittings",
                        "name": "Expansion Control Valve",
                        "slug": "expansion-control-valve",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/expansion-control-valve"
                    },
                    {
                        "categoryId": "non-return-valve",
                        "parentId": "valves-fittings",
                        "name": "Non Return Valve",
                        "slug": "non-return-valve",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/non-return-valve"
                    },
                    {
                        "categoryId": "pressure-temp-relief-valve",
                        "parentId": "valves-fittings",
                        "name": "Pressure Temp Relief Valve",
                        "slug": "pressure-temp-relief-valve",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/pressure-temp-relief-valve"
                    },
                    {
                        "categoryId": "pressure-limiting-valve",
                        "parentId": "valves-fittings",
                        "name": "Pressure Limiting Valve",
                        "slug": "pressure-limiting-valve",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/pressure-limiting-valve"
                    },
                    {
                        "categoryId": "tempering-valve",
                        "parentId": "valves-fittings",
                        "name": "Tempering Valve",
                        "slug": "tempering-valve",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/tempering-valve"
                    },
                    {
                        "categoryId": "thermostatic-mixing-valves",
                        "parentId": "valves-fittings",
                        "name": "Thermostatic Mixing Valves ",
                        "slug": "thermostatic-mixing-valves",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/thermostatic-mixing-valves"
                    },
                    {
                        "categoryId": "water-metering",
                        "parentId": "valves-fittings",
                        "name": "Water Metering",
                        "slug": "water-metering",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/valves-fittings/water-metering"
                    }
                ],
                "_url": "/valves-fittings"
            },
            {
                "categoryId": "bathroom-accessories",
                "name": "Bathroom Accessories",
                "slug": "bathroom-accessories",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "care-accessories",
                        "parentId": "bathroom-accessories",
                        "name": "Care Accessories",
                        "slug": "care-accessories",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/bathroom-accessories/care-accessories"
                    },
                    {
                        "categoryId": "bathroom-holders",
                        "parentId": "bathroom-accessories",
                        "name": "Bathroom Holders",
                        "slug": "bathroom-holders",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/bathroom-accessories/bathroom-holders"
                    },
                    {
                        "categoryId": "shelves-cabinets",
                        "parentId": "bathroom-accessories",
                        "name": "Shelves Cabinets",
                        "slug": "shelves-cabinets",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/bathroom-accessories/shelves-cabinets"
                    },
                    {
                        "categoryId": "soap-accessories",
                        "parentId": "bathroom-accessories",
                        "name": "Soap Accessories",
                        "slug": "soap-accessories",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/bathroom-accessories/soap-accessories"
                    },
                    {
                        "categoryId": "toilet-roll-accessories",
                        "parentId": "bathroom-accessories",
                        "name": "Toilet Roll Accessories",
                        "slug": "toilet-roll-accessories",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/bathroom-accessories/toilet-roll-accessories"
                    },
                    {
                        "categoryId": "towel-accessories",
                        "parentId": "bathroom-accessories",
                        "name": "Towel Accessories",
                        "slug": "towel-accessories",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/bathroom-accessories/towel-accessories"
                    }
                ],
                "_url": "/bathroom-accessories"
            },
            {
                "categoryId": "spare-parts",
                "name": "Spare Parts",
                "slug": "spare-parts",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "toilet-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Toilet Spare Parts",
                        "slug": "toilet-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/toilet-spare-parts"
                    },
                    {
                        "categoryId": "tapware-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Tapware Spare Parts",
                        "slug": "tapware-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/tapware-spare-parts"
                    },
                    {
                        "categoryId": "shower-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Shower Spare Parts",
                        "slug": "shower-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/shower-spare-parts"
                    },
                    {
                        "categoryId": "basin-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Basin Spare Parts",
                        "slug": "basin-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/basin-spare-parts"
                    },
                    {
                        "categoryId": "kitchen-laundry-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Kitchen Laundry Spare Parts",
                        "slug": "kitchen-laundry-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/kitchen-laundry-spare-parts"
                    },
                    {
                        "categoryId": "bath-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Bath Spare Parts",
                        "slug": "bath-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/bath-spare-parts"
                    },
                    {
                        "categoryId": "valve-spare-parts",
                        "parentId": "spare-parts",
                        "name": "Valve Spare Parts",
                        "slug": "valve-spare-parts",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/valve-spare-parts"
                    },
                    {
                        "categoryId": "spare-parts-l2",
                        "parentId": "spare-parts",
                        "name": "Spare Parts",
                        "slug": "spare-parts-l2",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/spare-parts/spare-parts-l2"
                    }
                ],
                "_url": "/spare-parts"
            },
            {
                "categoryId": "brouchures",
                "name": "Brouchures",
                "slug": "brouchures",
                "depth": 0,
                "subCategories": [
                    {
                        "categoryId": "caroma-collections",
                        "parentId": "brouchures",
                        "name": "Caroma Collections",
                        "slug": "caroma-collections",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/brouchures/caroma-collections"
                    },
                    {
                        "categoryId": "methven-collections",
                        "parentId": "brouchures",
                        "name": "Methven Collections",
                        "slug": "methven-collections",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/brouchures/methven-collections"
                    },
                    {
                        "categoryId": "caroma-livewell",
                        "parentId": "brouchures",
                        "name": "Caroma Livewell",
                        "slug": "caroma-livewell",
                        "depth": 1,
                        "subCategories": [],
                        "_url": "/brouchures/caroma-livewell"
                    }
                ],
                "_url": "/brouchures"
            }
        ]
    };

    useEffect(() => {
        const handleProductAdded = (msg: string) => {
            if (msg === 'Product present') {
                setHideParentMsg(true);
            } else {
                setHideParentMsg(false);
            }
        };
        eventEmitter.on('productAdded', handleProductAdded);
        return () => {
            eventEmitter.removeListener('productAdded', handleProductAdded);
        };
    }, []);
    const productUploadSection = () => {
        return (
            <>
                <p className="text-left md:text-left w-full md:w-auto">
                    {!projectForm?.lineItems?.length && !hideParentMsg && (
                        <span>
                            You have not added any products to this project yet.
                        </span>
                    )}
                </p>
                <div className="w-full md:w-auto flex items-end gap-[4px]">
                    <div>
                        <p className='!mb-[20px] z-10 relative'>
                            <a
                                href='/images/files/example_spectool_upload_sku_list.csv'
                                className=' font-semibold text-[16px] text-[#090909] cursor-pointer'
                                download="example_spectool_upload_sku_list.csv"
                                data-tooltip-id="see-template-tooltip-addproducts"
                                data-tooltip-content="Excel template for uploading your project"
                            >
                                See Template
                            </a>
                            <Tooltip id="see-template-tooltip-addproducts" place="top" style={{ fontSize: '14px' }} />
                        </p>
                        <ProductUpload customerId={account?.accountId} projectId={projectForm?.shoppingListId} projectForm={projectForm} />
                    </div>
                    <ViewToggleButton viewMode={viewMode} toggleView={toggleView} />
                </div>

            </>
        );
    };
    return (
        <>
            <div className='flex flex-col items-stretch justify-start xl:bg-[#F3EDE9]'>
                {isMediumDesktop ? (
                    <div className={`flex flex-row gap-4 pb-0  mt-[-232px]`}>
                        {isMediumDesktop && <div className="desktop-facets lg:min-w-[280px] 2xl:min-w-[280px]"></div>}
                        <div className="pt-2.5 bg-white px-4 md:px-5 xl:px-2 2xl:px-[72px] flex flex-col md:flex-row items-start md:justify-between w-full gap-[4px]">
                            {productUploadSection()}
                        </div>
                    </div>
                ) : (
                    <>
                        {!projectForm?.lineItems?.length && !hideParentMsg && (
                            <p className="text-left md:text-left w-full md:w-auto">
                                <span>
                                    You have not added any products to this project yet.
                                </span>
                            </p>
                        )}
                    </>
                )}
                <ProjectBuilderProductListAlgoliaTastic
                    data={datavalue}
                    params={propValue.params}
                    searchParams={propValue.searchParams}
                    categories={propValue.categories}
                    projectForm={projectForm}
                    viewMode={viewMode}
                    toggleView={toggleView}
                    customerId={account?.accountId}
                    projectId={projectForm?.shoppingListId}
                    showProjectBuilderUpload={true}
                />
            </div>
        </>
    );
};

export default AddProducts;
